import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileDown, Search, UserPlus } from 'lucide-react';
import { OnlineAdmissionApi, OnlineAdmissionRow, OnlineAdmissionStatus } from '@/services/online-admission.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { AdmitOnlineAdmissionModal } from './AdmitOnlineAdmissionModal';
import { formatDate } from '@/lib/format';

const columnHelper = createColumnHelper<OnlineAdmissionRow>();

/** Online Admission — school-tier queue, matching legacy's admission list (Admit/PDF actions). Cross-school isolation is structural — see OnlineAdmissionController's doc comment. */
export function OnlineAdmissionsPage() {
  const [status, setStatus] = useState<OnlineAdmissionStatus | ''>('pending');
  const [includeArchived, setIncludeArchived] = useState(false);
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'appliedAt', defaultSortOrder: 'desc' });
  const [admitting, setAdmitting] = useState<OnlineAdmissionRow | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['online-admissions', status, includeArchived, dt.params],
    queryFn: () => OnlineAdmissionApi.list({ status: status || undefined, includeArchived, ...dt.params }),
    placeholderData: keepPreviousData,
  });

  const openPdf = async (id: string) => {
    const html = await OnlineAdmissionApi.pdfHtml(id);
    const w = window.open('', '_blank');
    if (!w) {
      alert('Please allow pop-ups to view the application PDF.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const columns = [
    columnHelper.accessor('name', { header: 'Name' }),
    columnHelper.display({
      id: 'class',
      header: 'Class · Section',
      cell: (ctx) => (ctx.row.original.className ? `${ctx.row.original.className} · ${ctx.row.original.sectionName ?? '—'}` : '—'),
    }),
    columnHelper.accessor('mobile', { header: 'Mobile', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('appliedAt', { header: 'Applied', cell: (ctx) => formatDate(ctx.getValue()) }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (ctx) => (
        <div className="flex items-center gap-1.5">
          {ctx.getValue() === 'admitted' ? <Badge tone="green">Admitted</Badge> : <Badge tone="amber">Pending</Badge>}
          {ctx.row.original.isArchivedImport && <Badge tone="slate">2024 archive</Badge>}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (ctx) => (
        <div className="flex items-center gap-3">
          {ctx.row.original.status === 'pending' && (
            <button className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800" onClick={() => setAdmitting(ctx.row.original)}>
              <UserPlus className="h-3.5 w-3.5" /> Admit
            </button>
          )}
          <button className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700" onClick={() => openPdf(ctx.row.original.id)}>
            <FileDown className="h-3.5 w-3.5" /> PDF
          </button>
        </div>
      ),
    }),
  ];

  return (
    <>
      <PageHeader title="Online Admission" description="Applications submitted through the school's public admission form." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={dt.searchInput} onChange={(e) => dt.setSearchInput(e.target.value)} placeholder="Search by name or mobile" className="pl-9" />
        </div>
        <Select className="!w-40" value={status} onChange={(e) => setStatus(e.target.value as any)}>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="admitted">Admitted</option>
        </Select>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-500">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Include 2024 archive imports
        </label>
      </div>

      <DataTable columns={columns} result={data} state={dt} loading={isFetching} emptyMessage="No applications found." getRowId={(row) => row.id} />

      <AdmitOnlineAdmissionModal admission={admitting} onClose={() => setAdmitting(null)} />
    </>
  );
}
